/**
 * Task 151-B — per-service-type package config model (frontend).
 *
 * The reference packages UI configures six fixed service *types*
 * (social / ads / influencer / production / content / brand), each with
 * its own settings form. V2 instead has agency-defined *services*, each
 * carrying a `system_capability_key`. We reconcile the two with a
 * `config.kind` discriminator: each included service line persists a
 * freeform `config` object (the additive 151-A `jsonb`) shaped as
 * `{ kind: PackageServiceType, ...typedFields }`.
 *
 * This module owns the raw↔typed normalization, the typed defaults, the
 * per-kind validation, and the `{ kind, ... }` (de)serialization. It is
 * pure (no React) so it is unit-testable in isolation, and it uses the
 * 151-A `@kmi/contracts` types as the single source of the per-kind
 * shapes + enum arrays. It does NOT call any API or persist anything.
 */

import {
  AD_PLATFORM_KEYS,
  ADS_PRICING_MODES,
  PACKAGE_SERVICE_TYPES,
  PRODUCTION_TYPE_KEYS,
  SOCIAL_CONTENT_STRATEGIES,
  SOCIAL_PLATFORM_KEYS,
  type AdsServiceConfig,
  type BrandServiceConfig,
  type ContentServiceConfig,
  type InfluencerServiceConfig,
  type PackageServiceConfigByType,
  type PackageServiceType,
  type ProductionServiceConfig,
  type SocialContentQuota,
  type SocialServiceConfig,
} from '@kmi/contracts';
import type { SystemCapabilityKey } from '../../api/types';

// ── Capability → default kind ────────────────────────────────────────

/**
 * Derives the default config `kind` for an agency service from its
 * primary `system_capability_key`. This is the LOWEST-priority resolver
 * — `resolveKind` calls it only when no canonical-by-name override
 * (Task 167) and no valid stored discriminator are available.
 * Everything not explicitly mapped falls back to `content` as a sane
 * default. Note that the `creative` overload (production vs. brand)
 * was historically resolvable via the now-removed kind dropdown
 * (Task 166B). Today: brand-kind services need a saved
 * `config.kind='brand'` to round-trip — there is no UI to flip
 * `creative` capability to brand at runtime.
 */
export function defaultKindForCapability(cap: SystemCapabilityKey): PackageServiceType {
  switch (cap) {
    case 'publishing':
      return 'social';
    case 'paid_media':
      return 'ads';
    case 'influencers':
      return 'influencer';
    case 'creative':
      return 'production';
    case 'content':
      return 'content';
    default:
      return 'content';
  }
}

// ── Low-level coercers ───────────────────────────────────────────────

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Finite number from a number or numeric string; otherwise `fallback`. */
function toFiniteNumber(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return fallback;
}

/** Non-negative finite number (clamped at 0); otherwise `fallback`. */
function toCount(v: unknown, fallback = 0): number {
  const n = toFiniteNumber(v, fallback);
  return n < 0 ? 0 : n;
}

/** Nullable non-negative number — used for optional ads amount / %. */
function toNullableCount(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v < 0 ? 0 : v;
  if (typeof v === 'string') {
    const n = Number(v);
    if (Number.isFinite(n)) return n < 0 ? 0 : n;
  }
  return null;
}

/** Strict boolean (a stored non-boolean normalizes to `false`). */
function toBool(v: unknown): boolean {
  return v === true;
}

function toStr(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Picks `v` if it is one of `allowed`, else `fallback`. */
function pickEnum<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  return typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

/** Filters an array to the allowed enum keys, de-duplicating + preserving order. */
function pickEnumArray<T extends string>(v: unknown, allowed: readonly T[]): T[] {
  if (!Array.isArray(v)) return [];
  const set = new Set<string>(allowed as readonly string[]);
  const out: T[] = [];
  const seen = new Set<string>();
  for (const item of v) {
    if (typeof item === 'string' && set.has(item) && !seen.has(item)) {
      seen.add(item);
      out.push(item as T);
    }
  }
  return out;
}

/** Free-text string array (brand extras): trims, drops empties, de-dupes. */
function toStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of v) {
    if (typeof item !== 'string') continue;
    const t = item.trim();
    if (t.length === 0 || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function normalizeQuota(v: unknown): SocialContentQuota {
  const o = isPlainObject(v) ? v : {};
  return {
    designs: toCount(o['designs']),
    videos: toCount(o['videos']),
    stories: toCount(o['stories']),
  };
}

// ── Per-kind normalizers (raw jsonb → typed) ─────────────────────────

function normalizeSocial(o: Record<string, unknown>): SocialServiceConfig {
  return {
    platforms: pickEnumArray(o['platforms'], SOCIAL_PLATFORM_KEYS),
    monthly: normalizeQuota(o['monthly']),
    weekly: normalizeQuota(o['weekly']),
    strategy: pickEnum(o['strategy'], SOCIAL_CONTENT_STRATEGIES, 'unified'),
    reply: toBool(o['reply']),
  };
}

function normalizeAds(o: Record<string, unknown>): AdsServiceConfig {
  return {
    pricing: pickEnum(o['pricing'], ADS_PRICING_MODES, 'fixed'),
    amount: toNullableCount(o['amount']),
    percentage: toNullableCount(o['percentage']),
    platforms: pickEnumArray(o['platforms'], AD_PLATFORM_KEYS),
    details: toStr(o['details']),
  };
}

function normalizeInfluencer(o: Record<string, unknown>): InfluencerServiceConfig {
  return {
    count: toCount(o['count']),
    category: toStr(o['category']),
    details: toStr(o['details']),
  };
}

function normalizeProduction(o: Record<string, unknown>): ProductionServiceConfig {
  return {
    photos: toCount(o['photos']),
    videos: toCount(o['videos']),
    types: pickEnumArray(o['types'], PRODUCTION_TYPE_KEYS),
  };
}

function normalizeContent(o: Record<string, unknown>): ContentServiceConfig {
  return {
    monthly_count: toCount(o['monthly_count']),
    caption: toBool(o['caption']),
    strategy: toBool(o['strategy']),
    seo: toBool(o['seo']),
  };
}

function normalizeBrand(o: Record<string, unknown>): BrandServiceConfig {
  return {
    logo: toBool(o['logo']),
    identity: toBool(o['identity']),
    guidelines: toBool(o['guidelines']),
    extras: toStringArray(o['extras']),
    notes: toStr(o['notes']),
  };
}

// ── Public API ───────────────────────────────────────────────────────

/**
 * Coerces an opaque raw `config` value into the typed shape for `kind`,
 * dropping/repairing anything that does not fit (missing keys → typed
 * defaults; garbage → defaults; unknown enum members → filtered out).
 * The stored `kind` discriminator is ignored here — the caller supplies
 * the authoritative `kind`.
 */
export function normalizeConfig<K extends PackageServiceType>(
  kind: K,
  raw: unknown,
): PackageServiceConfigByType[K] {
  const o = isPlainObject(raw) ? raw : {};
  switch (kind) {
    case 'social':
      return normalizeSocial(o) as PackageServiceConfigByType[K];
    case 'ads':
      return normalizeAds(o) as PackageServiceConfigByType[K];
    case 'influencer':
      return normalizeInfluencer(o) as PackageServiceConfigByType[K];
    case 'production':
      return normalizeProduction(o) as PackageServiceConfigByType[K];
    case 'content':
      return normalizeContent(o) as PackageServiceConfigByType[K];
    case 'brand':
      return normalizeBrand(o) as PackageServiceConfigByType[K];
    default:
      // Exhaustive over PackageServiceType; defensively fall back.
      return normalizeContent(o) as PackageServiceConfigByType[K];
  }
}

/** The typed default config for `kind` (all-empty / zero baseline). */
export function defaultConfig<K extends PackageServiceType>(
  kind: K,
): PackageServiceConfigByType[K] {
  return normalizeConfig(kind, {});
}

/**
 * Builds the persisted `config` object: the typed fields plus the
 * `kind` discriminator. The result is a plain JSON object suitable for
 * the 151-A `config jsonb` (and the EF's object CHECK).
 */
export function serializeConfig<K extends PackageServiceType>(
  kind: K,
  cfg: PackageServiceConfigByType[K],
): Record<string, unknown> {
  return { kind, ...cfg };
}

/**
 * Reads the `kind` discriminator off a raw config object, if present and
 * valid. Returns `null` when absent/garbage so the caller can fall back
 * to `defaultKindForCapability`.
 */
export function readKind(raw: unknown): PackageServiceType | null {
  if (!isPlainObject(raw)) return null;
  const k = raw['kind'];
  return typeof k === 'string' && (PACKAGE_SERVICE_TYPES as readonly string[]).includes(k)
    ? (k as PackageServiceType)
    : null;
}

/**
 * Task 167 — Canonical kind for first-class default services. Matches the
 * `bootstrap-agency-services` EF's `DEFAULT_SERVICES` list (and the
 * `SERVICE_PRESETS` table in `constants.ts`). When a service name matches
 * one of these, this canonical kind WINS over both the saved `config.kind`
 * AND the capability default — so a service the user thinks of as "social
 * media management" always renders the Social typed-config UX, even if
 * the bootstrap wired it with `system_capability_key='content'` (which
 * `defaultKindForCapability` maps to kind=`content` → family=
 * `content_production`, which would render the generic deliverables
 * editor instead of `SocialSection`).
 *
 * Custom and non-canonical services return `null` and flow through the
 * existing `readKind` → `defaultKindForCapability` resolution unchanged.
 */
const CANONICAL_KIND_BY_SERVICE_NAME: Readonly<Record<string, PackageServiceType>> = {
  'إدارة السوشال ميديا والمحتوى': 'social',
  'إدارة الإعلانات المدفوعة': 'ads',
  'إدارة حملات المؤثرين': 'influencer',
  'إنتاج المحتوى والإبداع': 'production',
};

export function canonicalKindForService(serviceName: string): PackageServiceType | null {
  return CANONICAL_KIND_BY_SERVICE_NAME[serviceName] ?? null;
}

/**
 * Resolves the authoritative kind for a raw config. Priority order:
 *   1. Task 167 — canonical kind for first-class default services
 *      (looked up by name), if `serviceName` is provided and matches.
 *   2. The stored `kind` discriminator on the config, if valid.
 *   3. The capability-derived default.
 *
 * The name-based canonical override fixes the post-166B regression
 * where `إدارة السوشال ميديا والمحتوى` rendered as Content Production
 * because its primary capability `content` resolved to kind=`content`.
 * Without the override, the only way to correct the kind was the
 * destructive UI dropdown that Task 166B removed.
 *
 * For services not in the canonical list, the saved kind / capability
 * default behavior is unchanged.
 */
export function resolveKind(
  raw: unknown,
  cap: SystemCapabilityKey,
  serviceName?: string,
): PackageServiceType {
  if (serviceName !== undefined) {
    const canonical = canonicalKindForService(serviceName);
    if (canonical !== null) return canonical;
  }
  return readKind(raw) ?? defaultKindForCapability(cap);
}

/**
 * Inline coherence issues for a typed config (Arabic, display-only).
 * Empty array = coherent. These are advisory hints surfaced under each
 * section; they do not block save (the EF + DB CHECK are the hard
 * guards).
 */
export function validateConfig<K extends PackageServiceType>(
  kind: K,
  cfg: PackageServiceConfigByType[K],
): string[] {
  const issues: string[] = [];
  switch (kind) {
    case 'social': {
      const c = cfg as SocialServiceConfig;
      if (c.platforms.length === 0) issues.push('حدّد منصّة واحدة على الأقل لإدارة السوشال ميديا.');
      break;
    }
    case 'ads': {
      const c = cfg as AdsServiceConfig;
      if ((c.pricing === 'fixed' || c.pricing === 'hybrid') && (c.amount === null || c.amount <= 0))
        issues.push('أدخل المبلغ الثابت للإعلانات.');
      if (
        (c.pricing === 'percentage' || c.pricing === 'hybrid') &&
        (c.percentage === null || c.percentage <= 0)
      )
        issues.push('أدخل نسبة الإعلانات.');
      if (c.platforms.length === 0) issues.push('حدّد منصّة إعلانات واحدة على الأقل.');
      break;
    }
    case 'influencer': {
      const c = cfg as InfluencerServiceConfig;
      if (c.count <= 0) issues.push('حدّد عدد المؤثرين.');
      break;
    }
    case 'production': {
      const c = cfg as ProductionServiceConfig;
      if (c.photos <= 0 && c.videos <= 0) issues.push('حدّد عدد الصور أو الفيديوهات.');
      break;
    }
    case 'content': {
      const c = cfg as ContentServiceConfig;
      if (c.monthly_count <= 0) issues.push('حدّد عدد المنشورات/المقالات الشهرية.');
      break;
    }
    case 'brand': {
      const c = cfg as BrandServiceConfig;
      if (!c.logo && !c.identity && !c.guidelines && c.extras.length === 0)
        issues.push('اختر عنصراً واحداً على الأقل ضمن الهوية البصرية.');
      break;
    }
    default:
      break;
  }
  return issues;
}

/**
 * Task 280F (Phase 1A) — true when a typed config carries non-zero, meaningful
 * values (the agency actually configured the service). Used to warn when a
 * service is configured but NOT included — because an unincluded service does
 * not grant the client capabilities and is skipped by content-gen (Task 280B).
 * Pure; takes an already-normalized config (same contract as `validateConfig`).
 */
export function hasMeaningfulConfig<K extends PackageServiceType>(
  kind: K,
  cfg: PackageServiceConfigByType[K],
): boolean {
  switch (kind) {
    case 'social': {
      const c = cfg as SocialServiceConfig;
      return (
        c.platforms.length > 0 ||
        c.monthly.designs > 0 ||
        c.monthly.videos > 0 ||
        c.monthly.stories > 0
      );
    }
    case 'ads': {
      const c = cfg as AdsServiceConfig;
      return (c.amount ?? 0) > 0 || (c.percentage ?? 0) > 0 || c.platforms.length > 0;
    }
    case 'influencer': {
      const c = cfg as InfluencerServiceConfig;
      return c.count > 0;
    }
    case 'production': {
      const c = cfg as ProductionServiceConfig;
      return c.photos > 0 || c.videos > 0 || c.types.length > 0;
    }
    case 'content': {
      const c = cfg as ContentServiceConfig;
      return c.monthly_count > 0;
    }
    case 'brand': {
      const c = cfg as BrandServiceConfig;
      return c.logo || c.identity || c.guidelines || c.extras.length > 0;
    }
    default:
      return false;
  }
}

// Task 166B + 167 — the `PACKAGE_SERVICE_TYPE_LABELS_AR` export that
// once fed the kind-override `<select>` in advanced is gone. There is
// no UI today that maps the six raw `PackageServiceType` discriminators
// to a user-facing label; the drawer renders the family-derived label
// (`PACKAGE_SERVICE_FAMILY_LABELS_AR`) instead. If a future surface
// needs the per-kind labels, reintroduce them with a per-surface name
// and tests — DO NOT reintroduce as a feed for a kind-override dropdown.
